import z from "zod";
import { Chain, ChainId } from "./chain";
import { WaffleFlag } from "./waffleFlag";
import { WebhookType } from "./webhook";

/**
 * These enum values (currently) match the NaaS DB values, though that may not
 * be guaranteed in the future.
 *
 * The enum values do *not* match the protobuf values ({@link https://github.com/OMGWINNING/protobufs/blob/master/src/main/proto/common.proto#L57 explanation}).
 *
 * But the keys MUST match the protobuf keys in order for us to move towards compatibility with dynamic config.
 */
export enum Network {
  ETH_MAINNET = 0,
  ETH_ROPSTEN = 1,
  ETH_RINKEBY = 2,
  ETH_KOVAN = 3,
  ETH_GOERLI = 4,
  ETH_SEPOLIA = 5,
  ETH_HOLESKY = 6,
  FLOW_MAINNET = 300,
  FLOW_TESTNET = 301,
  ARB_MAINNET = 201,
  ARB_RINKEBY = 203,
  ARB_GOERLI = 205,
  ARB_SEPOLIA = 206,
  MATIC_MAINNET = 401,
  MATIC_MUMBAI = 406,
  MATIC_AMOY = 407,
  OPT_MAINNET = 501,
  OPT_KOVAN = 504,
  OPT_GOERLI = 505,
  OPT_SEPOLIA = 506,
  SOLANA_MAINNET = 701,
  SOLANA_DEVNET = 703,
  ASTAR_MAINNET = 801,
  POLYGONZKEVM_MAINNET = 901,
  POLYGONZKEVM_TESTNET = 902,
  POLYGONZKEVM_CARDONA = 903,
  STARKNET_MAINNET = 1001,
  STARKNET_GOERLI = 1002,
  STARKNET_SEPOLIA = 1003,
  BASE_MAINNET = 1101,
  BASE_GOERLI = 1102,
  BASE_SEPOLIA = 1103,
  ZKSYNC_MAINNET = 1201,
  ZKSYNC_SEPOLIA = 1202,
  ZORA_MAINNET = 1301,
  ZORA_SEPOLIA = 1302,
  FRAX_MAINNET = 1401,
  FRAX_SEPOLIA = 1402,
  BNB_MAINNET = 1501,
  BNB_TESTNET = 1502,
  AVAX_MAINNET = 1601,
  AVAX_FUJI = 1602,
  ARBNOVA_MAINNET = 1701,
  ALCHEMY_MAINNET = 1801,
  ALCHEMY_SEPOLIA = 1802,
  ALCHEMY_INTERNAL = 1803,
  WORLDCHAIN_MAINNET = 1901,
  WORLDCHAIN_SEPOLIA = 1902,
  ZETACHAIN_MAINNET = 2001,
  ZETACHAIN_TESTNET = 2002,
  BLAST_MAINNET = 2101,
  BLAST_SEPOLIA = 2102,
  SCROLL_MAINNET = 2201,
  SCROLL_SEPOLIA = 2202,
  LINEA_MAINNET = 2301,
  LINEA_SEPOLIA = 2302,
  GNOSIS_MAINNET = 2401,
  GNOSIS_CHIADO = 2402,
  FANTOM_MAINNET = 2501,
  FANTOM_TESTNET = 2502,
  MANTLE_MAINNET = 2601,
  MANTLE_SEPOLIA = 2602,
  CELO_MAINNET = 2701,
  CELO_ALFAJORES = 2702,
  BERACHAIN_ARTIO = 2801,
  BERACHAIN_BARTIO = 2802,
  METIS_MAINNET = 2901,
  OPBNB_MAINNET = 3001,
  OPBNB_TESTNET = 3002,
  POLYNOMIAL_MAINNET = 3101,
  POLYNOMIAL_SEPOLIA = 3102,
  SHAPE_MAINNET = 3201,
  SHAPE_SEPOLIA = 3202,
  BOB_MAINNET = 3301,
  CROSSFI_TESTNET = 3401,
  IOTA_MAINNET = 3501,
  IOTA_TESTNET = 3502,
  MODE_MAINNET = 3601,
  MODE_SEPOLIA = 3602,
  MOONBASE_ALPHA = 3701,
  MOONBEAM_MAINNET = 3801,
  MOONRIVER_MAINNET = 3901,
  PALM_MAINNET = 4001,
  PALM_TESTNET = 4002,
  SHIDEN_MAINNET = 4101,
  SHIDEN_SHIBUYA = 4102,
  SEI_MAINNET = 4201,
  SEI_TESTNET = 4202,
  KINTO_MAINNET = 4301,
  PLUME_TESTNET = 4402,
  ANIME_SEPOLIA = 4502,
  RACE_MAINNET = 4601,
  RACE_SEPOLIA = 4602,
  APECHAIN_MAINNET = 4701,
  APECHAIN_CURTIS = 4702,
  APEX_MAINNET = 4801,
  BOSS_MAINNET = 4901,
  LENS_SEPOLIA = 5002,
  SONEIUM_MINATO = 5102,
}

/**
 * We should prioritize removing this, it looks likes it's only used by Webhooks.
 * https://github.com/OMGWINNING/protobufs/blob/master/src/main/proto/common.proto#L75
 */
export enum ProtoNetwork {
  NETWORK_UNSPECIFIED = 0,
  ETH_MAINNET = 1,
  ETH_ROPSTEN = 2,
  ETH_RINKEBY = 3,
  ETH_KOVAN = 4,
  ETH_GOERLI = 5,
  ETH_SEPOLIA = 6,
  ETH_HOLESKY = 7,
  CRO_MAINNET = 100,
  CRO_CROESEID = 101,
  ARB_MAINNET = 201,
  ARB_RINKEBY = 203,
  ARB_GOERLI = 205,
  ARB_SEPOLIA = 206,
  FLOW_MAINNET = 300,
  FLOW_TESTNET = 301,
  MATIC_MAINNET = 401,
  MATIC_MUMBAI = 406,
  MATIC_AMOY = 407,
  OPT_MAINNET = 501,
  OPT_KOVAN = 504,
  OPT_GOERLI = 505,
  OPT_SEPOLIA = 506,
  SOLANA_MAINNET = 701,
  SOLANA_TESTNET = 702,
  SOLANA_DEVNET = 703,
  ASTAR_MAINNET = 801,
  ASTAR_SHIDEN = 802,
  ASTAR_SHIBUYA = 803,
  POLYGONZKEVM_MAINNET = 901,
  POLYGONZKEVM_TESTNET = 902,
  POLYGONZKEVM_CARDONA = 903,
  STARKNET_MAINNET = 1001,
  STARKNET_GOERLI = 1002,
  STARKNET_SEPOLIA = 1003,
  BASE_MAINNET = 1101,
  BASE_GOERLI = 1102,
  BASE_SEPOLIA = 1103,
  ZKSYNC_MAINNET = 1201,
  ZKSYNC_SEPOLIA = 1202,
  ZORA_MAINNET = 1301,
  ZORA_SEPOLIA = 1302,
  FRAX_MAINNET = 1401,
  FRAX_SEPOLIA = 1402,
  BNB_MAINNET = 1501,
  BNB_TESTNET = 1502,
  AVAX_MAINNET = 1601,
  AVAX_FUJI = 1602,
  ARBNOVA_MAINNET = 1701,
  ALCHEMY_MAINNET = 1801,
  ALCHEMY_SEPOLIA = 1802,
  ALCHEMY_INTERNAL = 1803,
  WORLDCHAIN_MAINNET = 1901,
  WORLDCHAIN_SEPOLIA = 1902,
  ZETACHAIN_MAINNET = 2001,
  ZETACHAIN_TESTNET = 2002,
  BLAST_MAINNET = 2101,
  BLAST_SEPOLIA = 2102,
  SCROLL_MAINNET = 2201,
  SCROLL_SEPOLIA = 2202,
  LINEA_MAINNET = 2301,
  LINEA_SEPOLIA = 2302,
  GNOSIS_MAINNET = 2401,
  GNOSIS_CHIADO = 2402,
  FANTOM_MAINNET = 2501,
  FANTOM_TESTNET = 2502,
  MANTLE_MAINNET = 2601,
  MANTLE_SEPOLIA = 2602,
  CELO_MAINNET = 2701,
  CELO_ALFAJORES = 2702,
  BERACHAIN_ARTIO = 2801,
  BERACHAIN_BARTIO = 2802,
  METIS_MAINNET = 2901,
  OPBNB_MAINNET = 3001,
  OPBNB_TESTNET = 3002,
  POLYNOMIAL_MAINNET = 3101,
  POLYNOMIAL_SEPOLIA = 3102,
  SHAPE_MAINNET = 3201,
  SHAPE_SEPOLIA = 3202,
  BOB_MAINNET = 3301,
  CROSSFI_TESTNET = 3401,
  IOTA_MAINNET = 3501,
  IOTA_TESTNET = 3502,
  MODE_MAINNET = 3601,
  MODE_SEPOLIA = 3602,
  MOONBASE_ALPHA = 3701,
  MOONBEAM_MAINNET = 3801,
  MOONRIVER_MAINNET = 3901,
  PALM_MAINNET = 4001,
  PALM_TESTNET = 4002,
  SHIDEN_MAINNET = 4101,
  SHIDEN_SHIBUYA = 4102,
  SEI_MAINNET = 4201,
  SEI_TESTNET = 4202,
  KINTO_MAINNET = 4301,
  PLUME_TESTNET = 4402,
  ANIME_SEPOLIA = 4502,
  RACE_MAINNET = 4601,
  RACE_SEPOLIA = 4602,
  APECHAIN_MAINNET = 4701,
  APECHAIN_CURTIS = 4702,
  APEX_MAINNET = 4801,
  BOSS_MAINNET = 4901,
  LENS_SEPOLIA = 5002,
  SONEIUM_MINATO = 5102,
}

/**
 * Type alias for valid values of the {@link https://github.com/OMGWINNING/protobufs/blob/master/src/main/proto/common.proto#L257 proto NetworkIdentifier}
 * (Alchemy-wide network id).
 *
 * For now, we're syncing this to the naas `Network` enum keys, but once we're depending on the
 * Alchemy `DynamicConfigService` for network config, static typing won't be possible. So this will
 * likely just become a type alias for `string` (`type NetworkId = string`), and we'll have to validate
 * values against a set of valid `NetworkIds` fetched from the `DynamicConfigService` before casting
 * to `NetworkId`.
 */
export type NetworkId = keyof typeof Network;

/**
 * Static set of all valid `NetworkIds`. Once we're depending on the Alchemy `DynamicConfigService`,
 * we won't be able to maintain a static set of `NetworkIds`, so this object should be used sparingly
 * in the meantime.
 *
 * For now, it's mostly used as a placeholder for `NetworkId` validation and in tests.
 */
export const NETWORK_IDS: Set<NetworkId> = new Set(
  Object.keys(Network).filter((key): key is NetworkId => isNaN(Number(key))),
);

/**
 * Once we're relying on a `DynamicConfigService`, we won't be able to statically validate `NetworkIds`, so we'll
 * have to validate incoming string values at runtime against a set of valid `NetworkIds` fetched from the `DynamicConfigService`.
 *
 * This factory fn returns a Zod schema that validates a string as a `NetworkId`, given a `validator` (most likely a method on
 * a future `DynamicConfigService` that checks whether the value exists in a cache of valid `NetworkIds`).
 * @param validator Fn that returns `true` if the `networkId` is a `NetworkId`, `false` otherwise.
 */
export function networkIdSchemaFactory(
  validator: (value: string) => Promise<boolean>,
) {
  return z.string().transform(async (value, ctx) => {
    if (!(await validator(value))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Not a NetworkId",
      });

      return z.NEVER;
    }
    return value as NetworkId;
  });
}

export enum Availability {
  PRERELEASE = "prerelease",
  PUBLIC = "public",
  DEPRECATED = "deprecated",
}

export interface NetworkInfo {
  id: NetworkId;
  network: Network;
  chainId: ChainId;
  chain: Chain;
  // Display name
  name: string;
  // Kebab-case version of the API endpoint like eth-mainnet
  kebabCaseId: string;
  // URL of the public block explorer
  explorerUrl: string;
  // Chain ID of the network as defined by EIP-155 (see chainlist.org)
  // Non-EVM chains may not have a chain ID
  networkChainId?: number;
  // Release status of the network
  availability: Availability;
  // Feature flag that will make network show up even if isPublic is false
  publicOverrideFlag?: WaffleFlag;
  // Whether network is accessible only to paid teams
  isPaidOnly: boolean;
  // List of webhook types that can be created for this network
  supportedWebhookTypes: WebhookType[];
  // Enum used in the alchemy-sdk-js package, if supported (https://github.com/alchemyplatform/alchemy-sdk-js)
  sdkEnum?: string;
  // [Deprecated] Enum used in our protobufs
  protoNetwork: ProtoNetwork;
  // A map of custom { [version]: endpoint } pairs, defaults to { v2: 'v2' }
  apiVersionEndpoints?: Record<string, string>;
  // Gas Manager enabled for this network
  supportsGasPolicies: boolean;
  // Full URL of our official alchemy faucet for this network, if it exists
  faucetUrl?: string;
  // Whether the network is a testnet or not
  isTestNet: boolean;
  // Order to display the network in selectors
  order: number;
  // Currency used to denote gas & transaction value
  currency: string;
  // Transactions show in the mempool
  supportsMempool: boolean;
  // Apps with this chain support our reinforced transactions addon
  supportsReinforcedTxns: boolean;
  // Apps with this chain support websockets
  supportsWebsockets: boolean;
  // API for this chain is limited to only AA methods
  supportsOnlyAccountAbstraction?: boolean;
  // URL to the alchemy docs
  docsUrl: string;
}
